import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Download, Users, ArrowUpCircle, DollarSign, Landmark, Mail } from "lucide-react";
import { api, formatApiError, API_BASE } from "../../lib/api";
import { useAuth } from "../../lib/auth";

const RANGES = [7, 30, 90, 365];
const SOURCE_LABELS = { plans: "Plan upgrades", setup_fee: "Setup fees", templates: "Premium templates", deposits: "Vendor deposits", marketplace: "Marketplace" };
const USAGE_LABELS = { party_games: "Party games", live_streams: "Live streams", feed_posts: "Feed posts", rsvps: "RSVPs" };
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Tile({ label, value, sub, icon: Icon, testid }) {
  return (
    <div className="bg-white border border-[var(--line)] rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--muted)]">{label}</p>
        <span className="w-8 h-8 rounded-full bg-[var(--bg-warm)] text-[var(--gold)] flex items-center justify-center"><Icon className="w-4 h-4" /></span>
      </div>
      <p className="font-serif text-3xl">{value}</p>
      {sub && <p className="text-xs text-[var(--muted)] mt-1">{sub}</p>}
    </div>
  );
}

function Panel({ title, children, testid, className = "" }) {
  return (
    <div className={`bg-white border border-[var(--line)] rounded-2xl p-5 ${className}`} data-testid={testid}>
      <h3 className="font-serif text-xl mb-4">{title}</h3>
      {children}
    </div>
  );
}

function TopList({ rows, testid, empty }) {
  if (!rows.length) return <p className="text-sm text-[var(--muted)]">{empty}</p>;
  return (
    <div className="space-y-2" data-testid={testid}>
      {rows.map((r, i) => (
        <div key={r.id} className="flex items-center justify-between text-sm border-b border-[var(--line)]/60 pb-2" data-testid={`${testid}-row-${i}`}>
          <div><p className="font-medium">{i + 1}. {r.name}</p><p className="text-xs text-[var(--muted)]">{r.count} sale{r.count === 1 ? "" : "s"}</p></div>
          <div className="text-right"><p className="font-medium">{money(r.gross)}</p><p className="text-xs text-[var(--gold)]">cut {money(r.cut)}</p></div>
        </div>
      ))}
    </div>
  );
}

function Controls({ days, setDays, hide, setHide }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex rounded-full border border-[var(--line)] overflow-hidden" data-testid="insights-range">
        {RANGES.map((d) => (
          <button key={d} onClick={() => setDays(d)} data-testid={`insights-range-${d}`}
            className={`px-4 py-1.5 text-xs transition-colors ${days === d ? "bg-[#111] text-white" : "bg-white hover:bg-[var(--bg-warm)]"}`}>{d === 365 ? "1 yr" : `${d}d`}</button>
        ))}
      </div>
      <label className="flex items-center gap-2 text-xs cursor-pointer">
        <input type="checkbox" checked={hide} onChange={(e) => setHide(e.target.checked)} data-testid="insights-hide-showcase" />
        Hide showcase demo weddings
      </label>
      <a href={`${API_BASE}/admin/insights.csv?days=${days}&hide_showcase=${hide}`} className="btn-outline-gold text-xs" data-testid="insights-download-csv">
        <Download className="w-3 h-3" /> Download CSV
      </a>
      <button onClick={async () => { try { const { data } = await api.post("/admin/insights/email-now"); data.ok ? toast.success(data.mocked ? `Email ready for ${data.to} (Resend is off, so it wasn't really sent)` : `Weekly email sent to ${data.to}`) : toast.error(data.message); } catch (e) { toast.error(formatApiError(e)); } }}
        className="btn-outline-gold text-xs" data-testid="insights-email-now"><Mail className="w-3 h-3" /> Email weekly report now</button>
    </div>
  );
}

export default function AdminInsightsPage() {
  const { user, loading } = useAuth();
  const [days, setDays] = useState(30);
  const [hide, setHide] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get(`/admin/insights?days=${days}&hide_showcase=${hide}`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [days, hide, user]);

  if (loading) return null;
  if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const revRows = data ? Object.entries(data.revenue).map(([k, v]) => ({ name: SOURCE_LABELS[k], ...v })) : [];
  return (
    <div data-testid="admin-insights">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Admin</p>
          <h1 className="font-serif font-light text-4xl">Insights</h1>
          <p className="text-[var(--muted)] mt-2 text-sm">Signups, upgrades, sales and usage — last {days} days{hide ? ", real customers only" : ", including showcase demos"}.</p>
        </div>
        <Controls days={days} setDays={setDays} hide={hide} setHide={setHide} />
      </div>

      {!data ? (
        <div className="py-20"><div className="w-8 h-8 border-2 border-[var(--gold)] border-t-transparent rounded-full animate-spin mx-auto" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Tile label="Signups" value={data.totals.signups} sub={Object.entries(data.signups_by_role).map(([k, v]) => `${v} ${k}`).join(" · ") || "No signups"} icon={Users} testid="insights-signups" />
            <Tile label="Plan upgrades" value={data.totals.upgrades} sub={Object.entries(data.upgrades_by_plan).map(([k, v]) => `${v} ${k}`).join(" · ") || "No upgrades"} icon={ArrowUpCircle} testid="insights-upgrades" />
            <Tile label="Total sales" value={money(data.totals.gross)} sub="Everything customers paid" icon={DollarSign} testid="insights-gross" />
            <Tile label="Your platform cut" value={money(data.totals.cut)} sub="What LockedInForever keeps" icon={Landmark} testid="insights-cut" />
          </div>

          <div className="grid lg:grid-cols-2 gap-4 mb-6">
            <Panel title="Sales vs your cut" testid="insights-revenue-chart">
              <div className="h-64">
                <ResponsiveContainer>
                  <AreaChart data={data.daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} minTickGap={20} />
                    <YAxis tick={{ fontSize: 10 }} width={40} />
                    <Tooltip formatter={(v) => money(v)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Area type="monotone" dataKey="gross" name="Total sales" stroke="#111" fill="#111" fillOpacity={0.08} />
                    <Area type="monotone" dataKey="cut" name="Platform cut" stroke="#C8A96B" fill="#C8A96B" fillOpacity={0.35} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Panel>
            <Panel title="Signups & upgrades" testid="insights-growth-chart">
              <div className="h-64">
                <ResponsiveContainer>
                  <BarChart data={data.daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} minTickGap={20} />
                    <YAxis tick={{ fontSize: 10 }} width={30} allowDecimals={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="signups" name="Signups" fill="#111" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="upgrades" name="Upgrades" fill="#C8A96B" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          </div>

          <Panel title="Revenue by source" testid="insights-revenue-sources" className="mb-6">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[var(--muted)]"><th className="py-2">Source</th><th>Sales</th><th className="text-right">Total sales</th><th className="text-right">Your cut</th></tr></thead>
                <tbody>
                  {revRows.map((r) => (
                    <tr key={r.name} className="border-t border-[var(--line)]/60" data-testid={`insights-source-${r.name.toLowerCase().replace(/\s+/g, "-")}`}>
                      <td className="py-2.5 font-medium">{r.name}</td><td>{r.count}</td><td className="text-right">{money(r.gross)}</td><td className="text-right text-[var(--gold)]">{money(r.cut)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          <div className="grid lg:grid-cols-3 gap-4">
            <Panel title="Feature usage" testid="insights-usage">
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(data.usage).map(([k, v]) => (
                  <div key={k} className="rounded-xl bg-[var(--bg-warm)] p-4" data-testid={`insights-usage-${k}`}>
                    <p className="font-serif text-2xl">{v}</p><p className="text-xs text-[var(--muted)]">{USAGE_LABELS[k]}</p>
                  </div>
                ))}
              </div>
            </Panel>
            <Panel title="Top marketplace sellers"><TopList rows={data.top_sellers} testid="insights-top-sellers" empty="No marketplace sales in this window." /></Panel>
            <Panel title="Top vendors (deposits)"><TopList rows={data.top_vendors} testid="insights-top-vendors" empty="No vendor deposits in this window." /></Panel>
          </div>

          <Panel title={`Homepage hero clicks · ${data.hero_clicks_total}`} testid="insights-hero-clicks" className="mt-4">
            <p className="text-xs text-[var(--muted)] mb-3">Every click on "See their wedding" for a featured couple in this window.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
              {data.hero_clicks.map((h, i) => (
                <div key={h.wedding_id} className="flex items-center justify-between text-sm border-b border-[var(--line)]/60 pb-2" data-testid={`insights-hero-row-${i}`}>
                  <span>{h.couple_names}{h.is_showcase && <span className="text-[10px] text-[var(--muted)]"> · showcase</span>}{h.opted_in && <span className="text-[10px] text-[var(--gold)]"> · opted in</span>}</span>
                  <span className="font-medium">{h.clicks}</span>
                </div>
              ))}
            </div>
            {!data.hero_clicks.length && <p className="text-sm text-[var(--muted)]">No hero clicks yet in this window.</p>}
          </Panel>
        </>
      )}
    </div>
  );
}
